import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@/components/common';
import { FormattedNumberInput } from '@/utils/number-format';
import { useUIStore } from '@/store/uiStore';
import { supplierApi } from '@/api/endpoints/suppliers';
import { CreateDiscountRequest, DiscountAllocation } from '@/types/supplier';

const DiscountModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, selectedSupplier, closeModal } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.discount;

  const [formData, setFormData] = useState<CreateDiscountRequest>({
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    description: '',
    executor: '',
    notes: '',
    allocateToInvoices: false,
    allocatedInvoices: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Mock invoice data for allocation
  const mockInvoices = [
    { id: 'INV-001', number: 'INV-001', amount: 5000000, remaining: 5000000 },
    { id: 'INV-002', number: 'INV-002', amount: 3000000, remaining: 3000000 },
    { id: 'INV-003', number: 'INV-003', amount: 2000000, remaining: 1500000 },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = t('validation.required');
    }
    if (!formData.executor.trim()) {
      newErrors.executor = t('validation.required');
    }
    if (formData.amount <= 0) {
      newErrors.amount = t('validation.positive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await supplierApi.createDiscount(selectedSupplier || '', formData);
      
      console.log('Discount created successfully');
      handleClose();
    } catch (error) {
      console.error('Error creating discount:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('discount');
    setFormData({
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      description: '',
      executor: '',
      notes: '',
      allocateToInvoices: false,
      allocatedInvoices: [],
    });
    setErrors({});
  };

  const handleInputChange = (field: keyof CreateDiscountRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAllocationChange = (invoiceId: string, amount: number) => {
    const allocations = formData.allocatedInvoices || [];
    const existingIndex = allocations.findIndex(a => a.invoiceId === invoiceId);
    
    if (amount > 0) {
      const invoice = mockInvoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        const allocation: DiscountAllocation = {
          invoiceId,
          invoiceNumber: invoice.number,
          allocatedAmount: amount,
        };
        
        if (existingIndex >= 0) {
          allocations[existingIndex] = allocation;
        } else {
          allocations.push(allocation);
        }
      }
    } else {
      if (existingIndex >= 0) {
        allocations.splice(existingIndex, 1);
      }
    }
    
    handleInputChange('allocatedInvoices', allocations);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('suppliers.discount.title')}
      size="lg"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Top row - Date and Executor as specified */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
              {t('suppliers.discount.date')} *
            </label>
            <input
              type="date"
              id="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className={`input-field ${errors.date ? 'border-red-500' : ''}`}
              disabled={loading}
            />
            {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
          </div>

          <div>
            <label htmlFor="executor" className="block text-sm font-medium text-gray-700 mb-1">
              {t('suppliers.discount.executor')} *
            </label>
            <input
              type="text"
              id="executor"
              value={formData.executor}
              onChange={(e) => handleInputChange('executor', e.target.value)}
              className={`input-field ${errors.executor ? 'border-red-500' : ''}`}
              placeholder={t('suppliers.discount.executor')}
              disabled={loading}
            />
            {errors.executor && <p className="text-red-500 text-xs mt-1">{errors.executor}</p>}
          </div>
        </div>

        {/* Discount amount - middle of modal as specified */}
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            {t('suppliers.discount.amount')} *
          </label>
          <FormattedNumberInput
            id="amount"
            value={formData.amount}
            onChange={(value) => handleInputChange('amount', value)}
            className={`input-field ${errors.amount ? 'border-red-500' : ''}`}
            placeholder="0"
            disabled={loading}
          />
          {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
        </div>

        {/* Note - below discount input as specified */}
        <div>
          <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
            {t('suppliers.discount.note')}
          </label>
          <textarea
            id="note"
            rows={3}
                          value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            className="input-field resize-none"
            placeholder={t('suppliers.discount.note')}
            disabled={loading}
          />
        </div>

        {/* Allocation checkbox - bottom-left as specified */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="allocateToInvoices"
            checked={formData.allocateToInvoices}
            onChange={(e) => handleInputChange('allocateToInvoices', e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="allocateToInvoices" className="ml-2 text-sm text-gray-700">
            {t('suppliers.discount.allocate')}
          </label>
        </div>

        {/* Invoice allocation table - below checkbox */}
        {formData.allocateToInvoices && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-900">Invoice Allocation</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {mockInvoices.map((invoice) => (
                <div key={invoice.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{invoice.number}</p>
                    <p className="text-xs text-gray-500">
                      Remaining: {formatCurrency(invoice.remaining)}
                    </p>
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      value={formData.allocatedInvoices?.find(a => a.invoiceId === invoice.id)?.allocatedAmount || 0}
                      onChange={(e) => handleAllocationChange(invoice.id, parseFloat(e.target.value) || 0)}
                      className="input-field text-sm"
                      placeholder="0"
                      min="0"
                      max={invoice.remaining}
                      step="1000"
                      disabled={loading}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons - bottom-right as specified */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {t('buttons.create')} {t('suppliers.discount.title')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default DiscountModal;
